import React, { useEffect, useState } from "react";
import {
    List,
    Avatar,
    Button,
    Card,
    Typography,
    Space,
    message,
    Spin,
    Row,
    Col,
    Tooltip,
    Skeleton,
    Empty,
    Flex,
    Input,
    Dropdown,
} from "antd";
import {
    DownOutlined,
    EyeOutlined,
    FilterOutlined,
    InboxOutlined,
    ReloadOutlined,
    SearchOutlined,
    UserOutlined,
} from "@ant-design/icons";
import api from "../../../api";
import ReportDetailsModal from "./ReportDetailsModal";

const { Text } = Typography;

function UserReports() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [filteredData, setFilteredData] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [pageSize, setPageSize] = useState(5);

    const [selectedReason, setSelectedReason] = useState("All");

    // ------------------- Fetch reports -------------------
    const fetchReports = async () => {
        try {
            setLoading(true);
            const requestData = {
                synceddatetime: "2025-05-29 10:08:00",
                FormCode: "201",
                AppVersion: "1.0.0.0",
                ApiKey: "kavin",
                AppTypeNo: "3",
            };
            const response = await api.getErrorReports(requestData);
            if (response.data.status === "1") {
                setReports(response.data.report_data);
            } else {
                message.warning("No reports found");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to load reports");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    const getReason = (reason) => {
        var str = "";
        if (reason) {
            for (var i = 0; i < reason.length; i++) {
                if (reason[i] === "$") str += ", ";
                else str += reason[i];
            }
            return str;
        }
    };

    // 🔹 Unique list of reasons
    const uniqueReasons = [...new Set(reports.map((r) => getReason(r.fld_reason_text)))];

    // 🔹 Dropdown menu for reason
    const reasonMenu = {
        items: [
            { key: "All", label: "All" },
            ...uniqueReasons.map((reason, index) => ({
                key: reason || `reason-${index}`,
                label: reason || "No Reason",
            })),
        ],
        onClick: ({ key }) => {
            setSelectedReason(key);
            setCurrentPage(1);
        },
    };

    // ------------------- Search filter -------------------
    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        const filtered = reports.filter((ev) =>
            ev.fld_reason_text.toLowerCase().includes(value)
        );
        setFilteredData(filtered);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setSearchTerm("");
        setFilteredData([]);
        setSelectedReason("All"); // 🔥 Reset reason filter
    };

    // ------------------- Combined Filters -------------------
    let dataToDisplay = searchTerm ? filteredData : reports;

    if (selectedReason !== "All") {
        dataToDisplay = dataToDisplay.filter(
            (ev) => getReason(ev.fld_reason_text) === selectedReason
        );
    }

    const startIndex = (currentPage - 1) * pageSize;
    const paginatedData = dataToDisplay.slice(startIndex, startIndex + pageSize);

    const handleView = (item) => {
        setSelectedReport(item);
        setIsModalVisible(true);
    };

    const ReportSkeleton = () => (
        <Card
            style={{
                borderRadius: 10,
                marginBottom: 10,
            }}
            bodyStyle={{
                padding: "10px 14px",
            }}
        >
            <Row align="middle">
                <Col flex="auto">
                    <Space size="middle" align="center">
                        <Skeleton.Avatar active size={40} />

                        <div>
                            <Skeleton.Input
                                active
                                size="small"
                                style={{
                                    width: 180,
                                    height: 16,
                                    marginBottom: 4,
                                }}
                            />
                            <br />
                            <Skeleton.Input
                                active
                                size="small"
                                style={{
                                    width: 120,
                                    height: 14,
                                }}
                            />
                        </div>

                        <Skeleton.Input
                            active
                            size="small"
                            style={{
                                width: 250,
                                height: 16,
                                marginLeft: 20,
                            }}
                        />

                        <Skeleton.Input
                            active
                            size="small"
                            style={{
                                width: 180,
                                height: 16,
                                marginLeft: 20,
                            }}
                        />
                    </Space>
                </Col>
            </Row>
        </Card>
    );

    return (
        <div className="review-page">
            {contextHolder}

            {/* 🔍 Header */}
            <Flex className="review-header" justify="space-between" align="center">
                <Input
                    className="review-search"
                    prefix={<SearchOutlined />}
                    placeholder="Search Reports..."
                    value={searchTerm}
                    onChange={handleSearch}
                />
            </Flex>

            <div className="review-container">
                <div className="review-heading">
                    <h2 className="review-title">User Reports</h2>
                    <div className="review-subtitle">Manage reports given by user</div>
                </div>

                {/* 🔘 Filters */}
                <div className="review-filters">
                    <div className="filter-buttons">
                        {/* <Button icon={<FilterOutlined />} /> */}
                        <Dropdown menu={reasonMenu} trigger={["click"]}>
                            <Button>
                                Reason: {selectedReason} <DownOutlined />
                            </Button>
                        </Dropdown>

                        <Button
                            icon={<ReloadOutlined />}
                            className="reset-btn"
                            onClick={handleResetFilters}
                        >
                            Reset Filter
                        </Button>
                    </div>
                </div>

                <div>
                    {loading ? (
                        <>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <ReportSkeleton key={index} />
                            ))}
                        </>
                    ) : dataToDisplay.length === 0 ? (
                        <Card className="nodata-card">
                            <Empty
                                description="No data available"
                                image={<InboxOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />}
                            />
                        </Card>
                    ) : (
                        <List
                            itemLayout="horizontal"
                            dataSource={paginatedData}
                            style={{ backgroundColor: "#fafafa" }}
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: dataToDisplay.length,
                                showSizeChanger: true,
                                pageSizeOptions: ["5", "10", "20"],
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                },
                            }}
                            renderItem={(item) => (
                                <List.Item
                                    key={item.fld_user_report_id}
                                    style={{
                                        cursor: "pointer",
                                        background: "#ffffff",
                                        borderRadius: 10,
                                        padding: "12px 16px",
                                        marginBottom: 10,
                                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                                    }}
                                    onClick={() => handleView(item)}
                                >
                                    <Row align="middle" justify="space-between" style={{ width: "100%" }}>
                                        <Col flex="auto">
                                            <Space align="center" size="large">
                                                <Space align="center">
                                                    <Avatar
                                                        src={item.reportedby_user_logo}
                                                        icon={<UserOutlined />}
                                                        size={48}
                                                    />
                                                    <Space direction="vertical" size={0}>
                                                        <Text strong>{item.reportedby_user_company_name}</Text>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            {item.reportedby_user_role_name}
                                                        </Text>
                                                    </Space>
                                                </Space>

                                                <Text style={{ marginLeft: 40 }}>
                                                    <strong>Reason:</strong>{" "}
                                                    {item.fld_reason_text
                                                        ? getReason(
                                                            item.fld_reason_text.length > 50
                                                                ? item.fld_reason_text.slice(0, 50) + "..."
                                                                : item.fld_reason_text
                                                        )
                                                        : "N/A"}
                                                </Text>



                                                <Text
                                                    type="secondary"
                                                    style={{ marginLeft: 40, fontSize: 12 }}
                                                >
                                                    Reported By: <strong>{item.user_company_name}</strong>
                                                </Text>
                                            </Space>
                                        </Col>

                                        {/* <Col>
                                            <Tooltip title="View Report Details">
                                                <Button
                                                    icon={<EyeOutlined />}
                                                    size="small"
                                                    style={{
                                                        borderRadius: 6,
                                                        borderColor: "#ccc",
                                                        color: "#555",
                                                        background: "white",
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleView(item);
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            </Tooltip>
                                        </Col> */}
                                    </Row>
                                </List.Item>
                            )}
                        />
                    )}

                    {isModalVisible && (
                        <ReportDetailsModal
                            isModalVisible={isModalVisible}
                            selectedReport={selectedReport}
                            setIsModalVisible={setIsModalVisible}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default UserReports;
